import * as React from 'react';
import { useState, useCallback } from 'react';
import { Button } from 'reactstrap';
import classnames from 'classnames';

interface UploadTabProps {
  isUploading: boolean;
  uploadProgress: number;
  uploadedImageData?: { url: string; width: number; height: number; file: File } | null;
  onFileSelect: (file: File) => void;
  onFileDrop: (file: File) => void;
  onUploadAnother?: () => void;
}

export const UploadTab: React.FC<UploadTabProps> = ({
  isUploading,
  uploadProgress,
  uploadedImageData,
  onFileSelect,
  onFileDrop,
  onUploadAnother
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (isImageFile(file)) {
        handleFileSelection(file);
      }
    }
  }, [onFileDrop]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (isImageFile(file)) {
        handleFileSelection(file);
      }
    }
  }, [onFileSelect]);

  const handleFileSelection = (file: File) => {
    // Trigger upload (modal will handle preview)
    onFileSelect(file);
  };

  const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/');
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (isUploading) {
    return (
      <div className="upload-tab">
        <div className="upload-progress">
          <div className="upload-icon">⏳</div>
          <h3>Uploading Image...</h3>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="progress-text">{uploadProgress}% complete</p>
        </div>
      </div>
    );
  }

  if (uploadedImageData) {
    return (
      <div className="upload-tab">
        <div className="upload-success">
          <div className="preview-container">
            <img src={uploadedImageData.url} alt="Upload preview" className="upload-preview" />
          </div>
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h3>Upload Complete!</h3>
            <p>Your image has been uploaded successfully</p>
            <p className="text-muted">
              Dimensions: {uploadedImageData.width} × {uploadedImageData.height}
            </p>
            <Button 
              color="secondary" 
              size="sm" 
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                if (onUploadAnother) {
                  onUploadAnother();
                }
              }}
            >
              Upload Another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-tab">
      <div className="upload-header">
        <h5>Upload an image</h5>
        <p className="text-muted">Drag and drop or browse to upload from your device</p>
      </div>

      <div
        className={classnames('upload-dropzone', {
          'drag-over': isDragOver
        })}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <div className="dropzone-content">
          <div className="upload-icon">📤</div>
          <h3>Drop your image here</h3>
          <p>or <strong>browse</strong> to choose a file</p>
          <div className="upload-specs">
            <small className="text-muted">
              Supports JPG, PNG, GIF up to 10MB
            </small>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="d-none"
      />

      <div className="upload-actions">
        <Button 
          color="outline-primary" 
          onClick={openFileDialog}
          disabled={isUploading}
        >
          📁 Browse Files
        </Button>
      </div>
    </div>
  );
};